module Client.Home.Index

open Client
open Elmish
open System
open Shared
open Illy.SafeStack.Client
open Illy.SafeStack.Client.jQueryUI
open SafeStack.Client
open Fable.Core

type Overlay =
    | NoOverlay
    | AdminMenu of Admin.Index.Model
    | IllyAdminMenu of IllyAdmin.Grid.Model
    | Article of NewsItem
    | TermsOfUse
    | UserGuides
    | IGMessage of string
    | UpdateTelephoneNumber of UpdateTelephoneNumber.Model
    | UpdateEmail of UpdateEmail.Model
    | UpdatePassword of UpdatePassword.Model
    | FailedLogins
    | LoggingOut

module Overlay =
    let isNoOverlay = function | NoOverlay -> true | _ -> false
    let isAdminMenu = function | AdminMenu _ -> true | _ -> false
    let isIllyAdminMenu = function | IllyAdminMenu _ -> true | _ -> false

type NewsArticleLoadingStatus =
    | Loading
    | LoadedSuccessfully
    | LoadedExternalUnavailable

module NewsArticleLoadingStatus =
    let isLoading =
        function
        | Loading -> true
        | LoadedSuccessfully
        | LoadedExternalUnavailable -> false

type Model =
    {
        Environment: EnvironmentDetails
        User: ClientSideUser
        Overlay: Overlay
        Applications: ApplicationType list
        AuthenticatingApp: ApplicationId option 
        Guides: Guide list
        NewsArticles: NewsItem list
        FailedLogins: Types.DatedFailure list
        LoadingEnvironment: bool
        LoadingApplications: bool
        LoadingGuides: bool
        LoadingNewsArticles: NewsArticleLoadingStatus
        LoadingFailedLogins: bool
    }

type Msg =
    | EnvironmentDataLoaded of EnvironmentDetails
    | AuthFailure of ApiError
    | ApiError of ApiError
    | AdminButtonToggled
    | UpdateEmailClicked
    | UpdatePasswordClicked
    | UpdateTelephoneNumberClicked
    | ApplicationListLoaded of ApplicationType list
    | NewsArticlesLoaded of NewsArticles
    | OpenArticle of NewsItem
    | CloseArticle
    | UpdateEmailMsg of UpdateEmail.Msg
    | UpdatePasswordMsg of UpdatePassword.Msg
    | UpdateTelephoneNumberMsg of UpdateTelephoneNumber.Msg
    | UserStorageUpdated
    | LogOutOnServer
    | LoggedOutOnServer of unit
    | LoggedOut
    | TickTimeSince
    | NotificationWasHidden
    | OpenTermsOfUse
    | CloseTermsOfUse
    | OpenUserGuides
    | CloseUserGuides
    | LoadedGuides of Guide list
    | UserGuideClicked of Guide
    | UserGuideDownloaded of unit
    | AdminMenuMsg of Admin.Index.Msg
    | FailedLoginsLoaded of Types.DatedFailure list
    | ViewFailedLogins
    | CloseFailedLogins
    | IGMessageLoaded of string option
    | CloseIGMessageOverlay
    | AuthenticateApp of Application
    | LaunchTokenApp of TokenApplication
    | AppAuthenticated of otp: Guid
    | TokenCalculated of string
    | IllyAdminButtonToggled
    | IllyAdminMsg of IllyAdmin.Grid.Msg

module Calls =
    let getEnvironment() = Api.secureCall<IHomeApi,_,_,_> SecuredRoute.Standard (fun api -> api.getEnvironment) () EnvironmentDataLoaded AuthFailure
    let listApplications() = Api.secureCall<IHomeApi,_,_,_> SecuredRoute.Standard (fun api -> api.listApplications) () ApplicationListLoaded ApiError
    let listGuides() = Api.secureCall<IHomeApi,_,_,_> SecuredRoute.Standard (fun api -> api.listGuides) () LoadedGuides ApiError
    let listNews() = Api.secureCall<IHomeApi,_,_,_> SecuredRoute.Standard (fun api -> api.listNews) () NewsArticlesLoaded ApiError
    let getFailedLogins() = Api.secureCall<IHomeApi,_,_,_> SecuredRoute.Standard (fun api -> api.getFailedLogins) () FailedLoginsLoaded ApiError
    let getIGMessage() = Api.secureCall<IHomeApi,_,_,_> SecuredRoute.Standard (fun api -> api.getIGMessage) () IGMessageLoaded ApiError
    let logout username = Api.secureCall SecuredRoute.Standard (fun api -> api.logout) username LoggedOutOnServer Msg.ApiError
    let authenticateApp appId = Api.secureCall<IHomeApi,_,_,_> SecuredRoute.Standard (fun api -> api.AuthenticateApp) appId AppAuthenticated ApiError
    let downloadGuide guideKey = Api.downloadFile<IHomeApi,_,_> SecuredRoute.Standard (fun api -> api.downloadGuide) guideKey UserGuideDownloaded Msg.ApiError
    let buildToken appId = Api.secureCall<IHomeApi,_,_,_> SecuredRoute.Standard (fun api -> api.BuildTokenUrl) appId TokenCalculated ApiError

let init() =
    {
        Environment = { Name = ""; Database = ""; TermsOfUse = None; SupportDetails = None }
        User = Storage.get<ClientSideUser> Storage.USER_STORAGE_KEY
        Overlay = NoOverlay
        Applications = []
        AuthenticatingApp = None
        NewsArticles = []
        Guides = []
        FailedLogins = []
        LoadingEnvironment = true
        LoadingApplications = true
        LoadingGuides = true
        LoadingNewsArticles = NewsArticleLoadingStatus.Loading
        LoadingFailedLogins = true
    }, Cmd.batch [
        Calls.getEnvironment()
        Calls.listApplications()
        Calls.listGuides()
        Calls.listNews()
        Calls.getFailedLogins()
        Calls.getIGMessage()
    ]

let dispatchUpdateEmailMsg model msg =
    match model.Overlay with
    | Overlay.UpdateEmail em ->
        let m, eff = UpdateEmail.update msg em
        match eff with
        | SideEffect.Cmd c -> {model with Overlay = UpdateEmail m}, Cmd.map UpdateEmailMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | UpdateEmail.Event.ApiError err ->
                {model with Overlay = UpdateEmail m}, Cmd.ofMsg (ApiError err) |> SideEffect.Cmd
            | UpdateEmail.Event.CloseOverlay ->
                {model with Overlay = NoOverlay}, SideEffect.none
            | UpdateEmail.Event.SaveSuccessful (u, notification) ->
                {model with
                    User = u
                    Overlay = NoOverlay
                },
                Cmd.batch [
                    Cmd.OfAsync.perform (Storage.store Storage.USER_STORAGE_KEY) u (fun () -> UserStorageUpdated)
                    Notification.success notification NotificationWasHidden
                ] |> SideEffect.Cmd
    | _ -> model, SideEffect.none

let dispatchUpdatePasswordMsg model msg =
    match model.Overlay with
    | Overlay.UpdatePassword em ->
        let m, eff = UpdatePassword.update msg em
        match eff with
        | SideEffect.Cmd c -> {model with Overlay = UpdatePassword m}, Cmd.map UpdatePasswordMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | UpdatePassword.Event.ApiError err ->
                {model with Overlay = UpdatePassword m}, Cmd.ofMsg (ApiError err) |> SideEffect.Cmd
            | UpdatePassword.Event.CloseOverlay ->
                {model with Overlay = NoOverlay}, SideEffect.none
            | UpdatePassword.Event.SaveSuccessful (u, notification) ->
                {model with
                    User = u
                    Overlay = NoOverlay
                },
                Cmd.batch [
                    Cmd.OfAsync.perform (Storage.store Storage.USER_STORAGE_KEY) u (fun () -> UserStorageUpdated)
                    Notification.success notification NotificationWasHidden
                ] |> SideEffect.Cmd
    | _ -> model, SideEffect.none

let dispatchUpdateTelephoneNumberMsg model msg =
    match model.Overlay with
    | UpdateTelephoneNumber etn ->
        let m, eff = UpdateTelephoneNumber.update msg etn
        match eff with
        | SideEffect.Cmd c -> {model with Overlay = UpdateTelephoneNumber m}, Cmd.map UpdateTelephoneNumberMsg c |> SideEffect.Cmd
        | SideEffect.Event ev ->
            match ev with
            | UpdateTelephoneNumber.Event.ApiError err ->
                {model with Overlay = UpdateTelephoneNumber m}, Cmd.ofMsg (ApiError err) |> SideEffect.Cmd
            | UpdateTelephoneNumber.Event.CloseOverlay ->
                {model with Overlay = NoOverlay}, SideEffect.none
            | UpdateTelephoneNumber.Event.SaveSuccessful (u, notification) ->
                {model with
                    User = u
                    Overlay = NoOverlay
                },
                Cmd.batch [
                    Cmd.OfAsync.perform (Storage.store Storage.USER_STORAGE_KEY) u (fun () -> UserStorageUpdated)
                    Notification.success notification NotificationWasHidden
                ] |> SideEffect.Cmd
    | _ -> model, SideEffect.none

let dispatchAdminMsg model msg =
    match model.Overlay with
    | AdminMenu ad ->
        let m, eff = Admin.Index.update msg ad
        match eff with
        | SideEffect.Cmd c -> {model with Overlay = AdminMenu m}, Cmd.map AdminMenuMsg c |> SideEffect.Cmd
        | SideEffect.Event (Admin.Index.Event.AppError err) -> {model with Overlay = AdminMenu m}, SideEffect.Event err
        | SideEffect.Event Admin.Index.Event.RefreshPublishedNews ->
            {model with
                Overlay = AdminMenu m
                LoadingNewsArticles = NewsArticleLoadingStatus.Loading
            }, Calls.listNews() |> SideEffect.Cmd
    | _ -> model, SideEffect.none

let findApp appId model =
    model.Applications
    |> List.pick (function Federated a when a.AppId = appId -> Some a | _ -> None)

let update msg model =
    match msg with
    | TickTimeSince ->
        // this only exists to refresh the view
        model, SideEffect.none
    | LoadedGuides guides ->
        {
            model with
                Guides = guides
                LoadingGuides = false
        }, SideEffect.none
    | NewsArticlesLoaded result ->
        match result with
        | NewsArticles.CombinedArticles articles
        | NewsArticles.LMArticlesOnly articles ->
            { model with
                NewsArticles = articles
                LoadingNewsArticles = NewsArticleLoadingStatus.LoadedSuccessfully
            }, SideEffect.none
        | NewsArticles.LMArticlesOnlyExternalUnavailable articles ->
            { model with
                NewsArticles = articles
                LoadingNewsArticles = NewsArticleLoadingStatus.LoadedExternalUnavailable
            }, SideEffect.none
    | OpenArticle article ->
        match article.Source with
        | NewsSource.Internal ->
            {
                model with Overlay = Article article
            }, SideEffect.none
        | NewsSource.External _ ->
            model, SideEffect.none
    | CloseArticle ->
        { model with Overlay = NoOverlay }, SideEffect.none
    | UserGuideClicked guide ->
        model, Calls.downloadGuide guide.Key |> SideEffect.Cmd
    | UserGuideDownloaded _ ->
        model, SideEffect.none
    | OpenTermsOfUse ->
        {
            model with
                Overlay = TermsOfUse
        }, SideEffect.none
    | CloseTermsOfUse ->
        {
            model with
                Overlay = NoOverlay
        }, SideEffect.none
    | OpenUserGuides ->
        { model with Overlay = UserGuides }, SideEffect.none
    | CloseUserGuides ->
        { model with Overlay = NoOverlay }, SideEffect.none
    | ApplicationListLoaded apps ->
        {
            model with
                Applications = apps
                AuthenticatingApp = None
                LoadingApplications = false
        }, SideEffect.none
    | EnvironmentDataLoaded env ->
        {
            model with
                Environment = env
                LoadingEnvironment = false
        }, SideEffect.none
    | IGMessageLoaded igMsg ->
        {model with
            Overlay =
                igMsg
                |> Option.map IGMessage
                |> Option.defaultValue model.Overlay
        }, SideEffect.none
    | CloseIGMessageOverlay ->
        {model with Overlay = NoOverlay}, SideEffect.none
    | AuthFailure err
    | ApiError err ->
        let newOverlay =
            match model.Overlay with
            | Overlay.LoggingOut ->
               Overlay.NoOverlay
            | _ ->
                model.Overlay

        { model with
            Overlay = newOverlay
            AuthenticatingApp = None
        }, SideEffect.Event (AppError.ApiError err)
    | AdminButtonToggled ->
        match model.Overlay with
        | NoOverlay ->
            let m, c = Admin.Index.init ()
            {model with Overlay = AdminMenu m}, Cmd.map AdminMenuMsg c |> SideEffect.Cmd
        | AdminMenu _ ->
            {model with Overlay = NoOverlay}, SideEffect.none
        | _ -> model, SideEffect.none
    | LoggedOutOnServer _ ->
        let lmUser =
            Cmd.OfAsync.perform
                (Storage.remove) 
                Storage.USER_STORAGE_KEY 
                (fun _ -> Msg.LoggedOut)

        let cpUser =
            Cmd.OfAsync.perform
                (Storage.remove) 
                Storage.CP_USER_STORAGE_KEY 
                (fun _ -> Msg.LoggedOut)

        model, Cmd.batch [ lmUser; cpUser ] |> SideEffect.Cmd
    | LogOutOnServer ->
        {model with
            Overlay = Overlay.LoggingOut
        }, Calls.logout model.User.Username |> SideEffect.Cmd
    | LoggedOut ->
        model, Routing.PageRoute.Login |> Routing.PageRoute.toPath |> Elmish.Navigation.Navigation.newUrl |> SideEffect.Cmd
    | UpdateEmailClicked ->
        let m, c = UpdateEmail.init()
        {model with Overlay = UpdateEmail m}, Cmd.map UpdateEmailMsg c |> SideEffect.Cmd
    | UpdateEmailMsg eMsg -> dispatchUpdateEmailMsg model eMsg
    | UpdatePasswordClicked ->
        let m, c = UpdatePassword.init()
        {model with Overlay = UpdatePassword m}, Cmd.map UpdatePasswordMsg c |> SideEffect.Cmd
    | UpdatePasswordMsg eMsg -> dispatchUpdatePasswordMsg model eMsg
    | UpdateTelephoneNumberClicked ->
        let m, c = UpdateTelephoneNumber.init()
        {model with Overlay = UpdateTelephoneNumber m}, Cmd.map UpdateTelephoneNumberMsg c |> SideEffect.Cmd
    | UpdateTelephoneNumberMsg tMsg -> dispatchUpdateTelephoneNumberMsg model tMsg
    | UserStorageUpdated -> model, SideEffect.none
    | NotificationWasHidden -> model, SideEffect.none
    | AdminMenuMsg aMsg -> dispatchAdminMsg model aMsg
    | FailedLoginsLoaded logins -> 
        {
            model with
                FailedLogins = logins
                LoadingFailedLogins = false
        }, SideEffect.none
    | ViewFailedLogins ->
        { model with Overlay = FailedLogins }, SideEffect.none
    | CloseFailedLogins ->
        { model with Overlay = NoOverlay }, SideEffect.none
    | AuthenticateApp app ->
        {model with AuthenticatingApp = Some app.AppId },
        Calls.authenticateApp app.AppId |> SideEffect.Cmd
    | AppAuthenticated otp ->
        match model.AuthenticatingApp with
        | None -> model, SideEffect.none
        | Some appId ->
            let app = model |> findApp appId  
            Browser.Dom.window.``open`` (url=sprintf "%s/%O" app.LandingUrl otp, target="_blank") |> ignore
            {model with AuthenticatingApp = None}, SideEffect.none
    | TokenCalculated url ->
        Browser.Dom.window.``open`` (url=url, target="_blank") |> ignore
        {model with AuthenticatingApp = None}, SideEffect.none
    | LaunchTokenApp app ->
        {model with AuthenticatingApp = Some app.AppId }, Calls.buildToken app.AppId |> SideEffect.Cmd
    | IllyAdminButtonToggled ->
        match model.Overlay with
        | NoOverlay ->
            let m, eff = IllyAdmin.Grid.init ()
            { model with Overlay =  IllyAdminMenu m }, eff |> Cmd.map IllyAdminMsg |> SideEffect.Cmd
        | IllyAdminMenu _ ->
            { model with Overlay = NoOverlay }, SideEffect.none
        | _ -> model, SideEffect.none
    | IllyAdminMsg iam ->
        match model.Overlay with
        | IllyAdminMenu m ->
            let m, eff = IllyAdmin.Grid.update iam m
            let newEff =
                match eff with
                | SideEffect.Cmd cmd ->
                    cmd |> Cmd.map IllyAdminMsg |> SideEffect.Cmd
                | SideEffect.Event event ->
                    SideEffect.Event event

            { model with Overlay = IllyAdminMenu m }, newEff
        | _ -> model, SideEffect.none
                
                

open Fable.React
open Fable.React.Props

let renderHeader model dispatch =
    header [] [
        div [ ClassName "left" ] [
            if model.User.Admin && (Overlay.isNoOverlay model.Overlay || Overlay.isAdminMenu model.Overlay) then 
                yield button [
                    Id "switchToAdminMode"
                    ClassName "ui-button ui-widget ui-state-default ui-corner-all ui-button-text-only"
                    OnClick (fun _ -> dispatch AdminButtonToggled)
                ] [
                    span [ ClassName "ui-button-text" ] [ str (match model.Overlay with | AdminMenu  _ -> "Exit" | _ -> "Admin") ] 
                ]
            if model.User.IsIlly && (Overlay.isNoOverlay model.Overlay || Overlay.isIllyAdminMenu model.Overlay) then 
                yield button [
                    Id "switchToIllyAdminMode"
                    ClassName "ui-button ui-widget ui-state-default ui-corner-all ui-button-text-only"
                    OnClick (fun _ -> dispatch IllyAdminButtonToggled)
                ] [
                    span [ ClassName "ui-button-text" ] [ str (match model.Overlay with | IllyAdminMenu  _ -> "Exit" | _ -> "Illy Admin") ] 
                ]
        ]
        span [ 
            ClassName "env" 
        ] [
            str (model.Environment.Name)
        ]
        div [] []
    ]

let renderUserCard model dispatch =
    let url = sprintf "https://www.gravatar.com/avatar/%s" model.User.EmailHash
    div [ ClassName "card" ] [
        div [ ClassName "header card-title" ] [
            str "LINKS Account"
        ]
        img [
            Id "illy-logo"
            Src url
        ]
        div [ Id "card-content" ] [
            span [ ClassName "primary" ] [
                str model.User.Forename
                str " "
                str model.User.Surname
            ] 
            br []
            span [ ClassName "secondary" ] [ str model.User.EmailAddress ] 
            br []
            span [ ClassName "tertiary clear"; Style [ Display DisplayOptions.Block ] ] [ str (model.User.Telephone |> Option.defaultValue "(No phone number)") ] 
            span [ ClassName "tertiary" ] [ 
                str "User since "
                str  (model.User.CreationDate |> illyDate)
            ] 
            br []
            span [ 
                Id "accreditation"
                ClassName "tertiary" 
            ] [ 
                yield str "Accreditation ID: "
                yield str (model.User.AccreditationId |> Option.defaultValue "(No accreditation)")
            ] 
            br []
            div [ Id "updateContent" ] [
                yield a [ Href "http://en.gravatar.com" ] [
                    FontAwesome.solid "user" [ FontAwesome.Size FontAwesome.LG ] []
                ] |> Tooltip.create [ str "Update Profile Image" ]

                yield FontAwesome.regular "mobile" [ FontAwesome.Size FontAwesome.LG ] [
                    OnClick (fun _ -> dispatch UpdateTelephoneNumberClicked)
                ] |> Tooltip.create [ str "Update Phone Number" ]

                yield FontAwesome.solid "envelope" [ FontAwesome.Size FontAwesome.LG ] [
                    OnClick (fun _ -> dispatch UpdateEmailClicked)
                ] |> Tooltip.create [ str "Update Email" ]

                yield FontAwesome.solid "lock" [ FontAwesome.Size FontAwesome.LG ] [
                    OnClick (fun _ -> dispatch UpdatePasswordClicked)
                ] |> Tooltip.create [ str "Update Password" ]
            ]
            div [ ClassName "clear" ] []
        ]
        div [ ClassName "clear" ] []
    ]

let skeletonScreenApplication =
    {
        AppId = ApplicationId 1
        Name = "Application Name"
        AuthUrl = ""
        LandingUrl = ""
        Image = ""
        UserLevel = "Standard"
    }
let skeletonScreenApplications =
    [
        Federated skeletonScreenApplication
        Federated skeletonScreenApplication
        Federated skeletonScreenApplication
    ]

let renderFederatedApp app model dispatch =
    li [] [
        a [
            OnClick (fun _ -> if not model.LoadingApplications && model.AuthenticatingApp.IsNone then dispatch (AuthenticateApp app))
            match model.AuthenticatingApp with
            | Some _ ->
                ClassName "authenticating"
            | _ ->
                ()
        ] [
            if model.LoadingApplications then
                jQueryUI.skeletonScreenIcon
            else
                match model.AuthenticatingApp with
                | Some authenticatingApp when authenticatingApp = app.AppId ->
                    jQueryUI.saveSpinner
                | _ ->
                    img [ Src app.Image ]
            div [] [
                h2 [
                    Class (jQueryUI.addSkeletonScreen "" None model.LoadingApplications)
                ] [ str app.Name ]
                span [
                    Class (jQueryUI.addSkeletonScreen "" None model.LoadingApplications)
                ] [ str app.UserLevel ]
            ]
        ]
    ]

let renderTokenApp app model dispatch =
    li [] [
        a [
            OnClick (fun _ -> if not model.LoadingApplications && model.AuthenticatingApp.IsNone then dispatch (LaunchTokenApp app))
            match model.AuthenticatingApp with
            | Some _ ->
                ClassName "authenticating"
            | _ ->
                ()
        ] [
            if model.LoadingApplications then
                jQueryUI.skeletonScreenIcon
            else
                img [ Src app.Image ]
            div [] [
                h2 [
                    Class (jQueryUI.addSkeletonScreen "" None model.LoadingApplications)
                ] [ str app.Name ]
            ]
        ]
    ]

let renderApplications model dispatch =
    [
        h2 [] [ str "Applications" ]
        div [ ClassName "apps" ] [
            if model.Applications = [] && not model.LoadingApplications then
                div [ Id "noApps" ] [
                    str "You have not yet been granted access to any applications."
                ]
            else
                ul [ Id "applicationList" ] [
                    let apps = if model.LoadingApplications then skeletonScreenApplications else model.Applications
                    for app in apps do
                        match app with
                        | Federated a -> renderFederatedApp a model dispatch
                        | TokenBased a -> renderTokenApp a model dispatch
                ]
        ]
    ]

let renderEnvironment model dispatch =
    
    div [ Id "environment" ] [
        h2 [] [ str "Environment" ]
        str "LINKS Manager is running against "
        strong [
            Class (jQueryUI.addSkeletonScreen "" (Some jQueryUI.fixedWidth) model.LoadingEnvironment)
        ] [ str model.Environment.Database ]
        str "."
        br []
        str "This is the "
        strong [
            Class (jQueryUI.addSkeletonScreen "" (Some jQueryUI.fixedWidth) model.LoadingEnvironment)
        ] [ str model.Environment.Name ]
        str " environment."
    ]

let renderFailedLogins model dispatch =
    jQueryUI.modal
        "Failed Login Attempts (Last 3 days)."
        50
        CloseFailedLogins
        dispatch
        (Some 600)
        [
            jQueryUI.simpleTable
                "There are no failed logins."
                [
                    jQueryUI.ColumnMetaData.createWithHeader [ str "Date" ] (fun (x: Types.DatedFailure) -> [ str (illyDateTime x.Date) ])
                    jQueryUI.ColumnMetaData.createWithHeader [ str "IP Address" ] (fun (x: Types.DatedFailure) -> [ str x.IPAddress ])
                ]
                false
                model.FailedLogins
        ]

let renderLoggingOut model dispatch =
    jQueryUI.modal
        "Logging Out"
        50
        ()
        ignore
        (Some 600)
        [
            p [] [str "Waiting for the system to log you out..."]
            div [Style [Display DisplayOptions.Flex; Width "100%"; JustifyContent "center"; MarginBottom "1em"]] [
                jQueryUI.spinner
            ]
        ]


let renderProfile model dispatch =
    div [ Id "profile" ] [
        h2 [] [ str "User Profile" ]
        div [ ClassName "top" ] [
            div [ ClassName "summary ui-helper-clearfix" ] [
                span [] [
                    str "You are logged in as "
                    em [] [ str model.User.Username ]
                    str "."
                ]
                jQueryUI.buttonWithClass (Some "ui-state-default") "Log out" LogOutOnServer dispatch
            ]
            div [
                ClassName (jQueryUI.addSkeletonScreen "attempts" None model.LoadingFailedLogins)
            ] [
                match model.FailedLogins with
                | [] when not model.LoadingFailedLogins -> ()
                | logins -> 
                    yield str "There have been "
                    if not model.LoadingFailedLogins then 
                        yield a [ 
                            Href "javascript:void(0)" 
                            OnClick (fun _ -> dispatch ViewFailedLogins)
                        ] [
                            str (logins.Length.ToString())
                            str " failed login attempt(s)"
                        ]
                    yield str " in the last 72 hours."
            ]
            div [ ClassName "stats" ] [
                yield str "Last login was "
                yield str (model.User.LastLogin |> Option.map illyDateTime |> Option.defaultValue "Never")
                
                match model.User.LastLogin with
                | None -> yield str " (not logged on before)"
                | Some last ->
                    yield str " ("
                    yield str (Moment.fromNow last)
                    yield str ")"
            ]
            div [ ClassName "user" ] [
                renderUserCard model dispatch
            ]
        ]
        yield! renderApplications model dispatch
    ]

let renderFooter model dispatch =
    footer [] [
        ul [] [
            match model.Environment.TermsOfUse with
            | None -> ()
            | Some _ ->
                yield li [] [
                    a [ 
                        Target "_blank"
                        OnClick (fun _ -> dispatch OpenTermsOfUse)
                    ] [ str "Terms of use"]
                ]

            
            match model.Environment.SupportDetails with
            | None -> ()
            | Some support -> 
                yield li [] [ 
                    str "Support: " 
                    str support
                ]
            yield li [] [
                a [ Target "_blank"; Href "https://illy-servicedesk.atlassian.net/servicedesk/customer/portal/1" ] [ str "ILLY Online" ]
            ]
            yield li [] [
                a [ Target "_blank"; Href $"%s{Route.LINKS_MANAGER_PATH}/policies/Privacy policy - LINKS Carepath.pdf" ] [ str "Privacy Policy" ]
            ]
        ]
    ]

let skeletonGuide =
    {
        Key = ""
        Name = "User Guide Name"
    }

let skeletonNewsItem =
    {
        Id = 0u
        Headline = "Headline"
        Date = DateTime.Now
        Content = "Content"
        Author = "Author"
        Source = Internal
    }

let skeletonScreenNewsItems =
    [
        true, skeletonNewsItem
        true, skeletonNewsItem
        true, skeletonNewsItem
    ]

let markArticleAsRead newsItem =
    let seenArticles = Storage.tryGet Storage.SEEN_NEWS_ARTICLES_STORAGE_KEY |> Option.defaultValue []
    let articleId = newsItem |> NewsItem.toIdentifier
    if List.contains articleId seenArticles = false then
        let newSeenArticles = articleId :: seenArticles
        Storage.store Storage.SEEN_NEWS_ARTICLES_STORAGE_KEY newSeenArticles
        |> Async.StartAsPromise
        |> Promise.start

let renderNews model dispatch =
    let loading = NewsArticleLoadingStatus.isLoading model.LoadingNewsArticles

    if model.NewsArticles.Length = 0 && not loading then
        [
            p [] [ str "There are no published news articles." ]
        ]
    else
        let seenArticles = Storage.tryGet Storage.SEEN_NEWS_ARTICLES_STORAGE_KEY |> Option.defaultValue []
        let newsArticles =
            if loading then
                skeletonScreenNewsItems
            else
                model.NewsArticles |> List.map (fun a ->
                    List.contains (NewsItem.toIdentifier a) seenArticles, a
                )
        [
            if model.LoadingNewsArticles = NewsArticleLoadingStatus.LoadedExternalUnavailable then
                yield div [ Id "noExternalArticleWarning" ] [ str "The external news feed could not be loaded - only local articles are being displayed" ]

            yield ol
                [ Id "news-list" ]
                (newsArticles
                |> List.map (fun (seenArticle, newsArticle) ->
                    li [
                        if not seenArticle then yield Class "new"
                    ] [
                        a [
                            match newsArticle.Source with
                            | External (_, link) ->
                                yield Href link
                                yield Target "_blank"
                                yield Rel "noopener noreferrer"
                            | Internal -> ()

                            yield OnClick (fun _ ->
                                    markArticleAsRead newsArticle
                                    newsArticle |> OpenArticle |> dispatch
                                )
                        ] [
                            h3 [ Class (jQueryUI.addSkeletonScreen "headline" None loading) ] [ str newsArticle.Headline ]
                            // Pre-sanitised on the server
                            div [ Class (jQueryUI.addSkeletonScreen "summary" None loading); DangerouslySetInnerHTML { __html = newsArticle.Content } ] []
                            div [ Class (jQueryUI.addSkeletonScreen "byline" None loading) ] [
                                yield span [] [
                                    yield newsArticle.Author |> str
                                    match newsArticle.Source with
                                    | External (title, _) ->
                                        yield i [ Style [ Display DisplayOptions.Block ] ] [
                                            str " via "
                                            str title
                                        ]
                                    | Internal -> ()
                                ]
                                if not loading then
                                    yield span [] [ newsArticle.Date |> illyDate |> str ]
                            ]
                        ]
                    ]
                ))
        ]

let subscribe model =
    let sub dispatch =
        let intervalId = Fable.Core.JS.setInterval (fun _ -> dispatch TickTimeSince) 60000
        { new IDisposable with
            member __.Dispose() = Fable.Core.JS.clearInterval intervalId
        }
    [ [ "tickTimerSince" ], sub ]

let renderOverlay model dispatch =
    div [
        Key "overlay-container"
    ] [
        match model.Overlay with
        | NoOverlay -> ()
        | TermsOfUse ->
            match model.Environment.TermsOfUse with
            | None -> ()
            | Some terms ->
                yield! jQueryUI.modal 
                    "Terms of Use" 
                    2000
                    CloseTermsOfUse
                    dispatch
                    (Some 600)
                    [
                        p [ DangerouslySetInnerHTML { __html = terms } ] []
                    ]
        | UserGuides ->
            yield! jQueryUI.modal
                "User Guides"
                2000
                CloseUserGuides
                dispatch
                (Some 600)
                [
                    div [ Id "guideTable" ] [
                        jQueryUI.simpleTable
                            "No user guides available"
                            [
                                Some [ str "Name" ] ===> fun (g: Guide) -> [ str g.Name ]
                                Some [ str "Download" ] ===> fun g -> [
                                    div [ ClassName "centre" ] [
                                        a [ OnClick (fun _ -> g |> UserGuideClicked |> dispatch) ] [
                                            FontAwesome.light "file-pdf" [] []
                                        ]
                                    ]
                                ]
                            ]
                            model.LoadingGuides
                            model.Guides
                    ]
                ]
        | IGMessage igMsg ->
            yield! jQueryUI.modal 
                "Greeting Message" 
                2000
                CloseIGMessageOverlay
                dispatch
                (Some 600)
                [
                    p [] [ str igMsg ]
                ]
        | Article newsArticle ->
            yield! jQueryUI.modal
                newsArticle.Headline
                2000
                CloseArticle
                dispatch
                (Some 1200)
                [
                    article [] [
                        div [ Class "byline" ] [
                                span [] [
                                    yield newsArticle.Author |> str
                                    match newsArticle.Source with
                                    | External (title, _) ->
                                        yield i [ Style [ Display DisplayOptions.Block ] ] [
                                            str " via "
                                            str title
                                        ]
                                    | Internal -> ()
                                ]
                                span [] [ newsArticle.Date |> illyDate |> str ]
                            ]
                        hr []
                        // Pre-sanitised on the server
                        div [ Class "content"; DangerouslySetInnerHTML { __html = newsArticle.Content }] []
                    ]
                ]
        | AdminMenu am ->
            yield Admin.Index.view am (AdminMenuMsg >> dispatch)
        | UpdateEmail ue ->
            yield! UpdateEmail.view ue (UpdateEmailMsg >> dispatch)
        | UpdatePassword ue ->
            yield! UpdatePassword.view ue (UpdatePasswordMsg >> dispatch)
        | UpdateTelephoneNumber utn ->
            yield! UpdateTelephoneNumber.view utn (UpdateTelephoneNumberMsg >> dispatch)
        | FailedLogins ->
            yield! renderFailedLogins model dispatch
        | LoggingOut ->
            yield! renderLoggingOut model dispatch
        | IllyAdminMenu iam ->
            yield IllyAdmin.Grid.view iam (IllyAdminMsg >> dispatch)
    ]

let renderMain model dispatch =
    main [] [
        div [ Id "profileAndEnv" ] [
            renderProfile model dispatch
            renderEnvironment model dispatch
        ] 
        div [ Id "news" ] [
            yield div [ Id "header" ] [
                h2 [] [ str "News" ]
                UI.button "User Guides" OpenUserGuides dispatch
            ]
            yield! renderNews model dispatch
        ]
    ]

let view model dispatch =
    div [
        ClassName "home_theme" 
        Style [ ZIndex 200 ]
    ] [
        yield renderHeader model dispatch

        match model.Overlay with
        | AdminMenu _
        | IllyAdminMenu _ ->
            yield renderOverlay model dispatch
        | _ ->
            yield renderOverlay model dispatch
            yield renderMain model dispatch

        yield renderFooter model dispatch
    ]     